import type { DesignData } from 'editor/src/store/design/types';

import classifyPrintableAreas from './classifyPrintableAreas';
import PrintableContentError from './PrintableContentError';

import type {
  ConfigurationState,
  ConfigurationStatus,
  PersonalizationPricingCapability,
  PersonalizationPricingMatch,
} from './types';

/**
 * Shared by the full editor and SDK: reports which print areas carry artwork. It decides no price
 * of its own — the host is the price authority — but it carries the host's answers back, so the
 * editor can gate checkout on the matched variant and show the price the buyer will pay.
 */
export default class ConfigurationSession {
  private capability?: PersonalizationPricingCapability;
  private sequence = 0;
  private state: ConfigurationState = {
    status: 'disabled',
    areaIds: [],
    sequence: 0,
  };

  private match: PersonalizationPricingMatch | null = null;
  /** False while the current configuration is still waiting for the host's match. */
  private hostAnswered = false;
  private readonly listeners = new Set<(state: ConfigurationState) => void>();
  private readonly matchListeners = new Set<(match: PersonalizationPricingMatch | null) => void>();

  private price: string | null = null;
  private readonly priceListeners = new Set<(price: string | null) => void>();

  configure(capability?: PersonalizationPricingCapability) {
    this.capability = capability;
    this.setState(capability ? 'pending' : 'disabled', []);
  }

  /** The host's answer to the configuration it names; null while it has none. */
  setMatch(match: PersonalizationPricingMatch | null) {
    this.applyMatch(match, true);
  }

  getMatch() {
    return this.match;
  }

  /**
   * The price the host renders for what is on screen, formatted in the buyer's market. It outlives
   * a dropped match: a host that cannot sell the configuration still quotes the cheapest one the
   * buyer could reach, and the editor shows that rather than nothing.
   */
  setPrice(price: string | null) {
    if (this.price === price) {
      return;
    }
    this.price = price;
    this.priceListeners.forEach((listener) => listener(this.price));
  }

  getPrice() {
    return this.price;
  }

  update(design: DesignData) {
    if (!this.capability) {
      return;
    }
    const { allowedAreaIds } = this.capability;
    // Spread names are the area ids; the alias map exists only because the classifier is shared with PHP.
    const areaAliases: Record<string, string> = {};
    allowedAreaIds.forEach((areaId) => {
      areaAliases[areaId] = areaId;
    });
    let areaIds: string[];
    try {
      areaIds = classifyPrintableAreas(design, areaAliases, allowedAreaIds);
    } catch (error) {
      if (!(error instanceof PrintableContentError)) {
        throw error;
      }
      // Digitizing and uploads are still in flight. The gallery image is already UPLOADED, so this
      // is the only signal; calling it invalid artwork shows a permanent failure during normal embroidery.
      if (error.incomplete) {
        this.setState('pending', []);
        return;
      }
      this.setState('error', [], error.message, 'invalid');
      return;
    }
    if (!areaIds.length) {
      this.setState('error', [], 'Add artwork to at least one print area.', 'empty');
      return;
    }
    this.setState('ready', areaIds);
  }

  /** Artwork is still being processed or the design is not loaded; a classification will follow. */
  invalidate() {
    if (this.capability) {
      this.setState('pending', []);
    }
  }

  fail(error: string) {
    if (this.capability) {
      this.setState('error', [], error, 'invalid');
    }
  }

  getState() {
    return this.state;
  }

  canSubmit() {
    return this.state.status === 'disabled' || this.state.status === 'ready';
  }

  /**
   * Checking out also needs the host's match for the configuration on screen: an embedded editor
   * saves the design against that variant, and a host that cannot sell these areas drops the
   * add-to-cart. A match made for an earlier configuration is no match at all.
   */
  canCheckout() {
    if (!this.capability) {
      return this.canSubmit();
    }

    return (
      this.state.status === 'ready' &&
      Boolean(this.match?.variantId) &&
      !this.match?.reason &&
      this.match?.sequence === this.state.sequence
    );
  }

  getBlockedReason() {
    if (!this.capability || this.canCheckout()) {
      return null;
    }
    if (this.state.status === 'error') {
      return this.state.reason ?? 'invalid';
    }
    // Pending only until the host answers. A legacy null answer means "not for sale" and used to
    // disable the button with no message; treating it as pending leaves "Checking this design…" up.
    if (this.state.status === 'pending' || !this.hostAnswered) {
      return 'pending';
    }
    if (this.match?.reason) {
      return this.match.reason;
    }
    if (this.match && !this.match.variantId) {
      return 'unsold';
    }
    return null;
  }

  subscribe(listener: (state: ConfigurationState) => void) {
    this.listeners.add(listener);
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  // Separate from `subscribe`, whose every emission is reported to the host: a match is the host's
  // own answer, and echoing it back would have it match again on what it just told us.
  subscribeMatch(listener: (match: PersonalizationPricingMatch | null) => void) {
    this.matchListeners.add(listener);
    listener(this.match);
    return () => {
      this.matchListeners.delete(listener);
    };
  }

  subscribePrice(listener: (price: string | null) => void) {
    this.priceListeners.add(listener);
    listener(this.price);
    return () => {
      this.priceListeners.delete(listener);
    };
  }

  private setState(status: ConfigurationStatus, areaIds: string[], error?: string, reason?: 'empty' | 'invalid') {
    const previous = this.state;
    if (
      previous.status === status &&
      previous.error === error &&
      previous.reason === reason &&
      JSON.stringify(previous.areaIds) === JSON.stringify(areaIds)
    ) {
      return;
    }
    this.sequence += 1;
    this.state = { status, areaIds, sequence: this.sequence };
    if (error !== undefined) {
      this.state.error = error;
    }
    if (reason !== undefined) {
      this.state.reason = reason;
    }
    // The match described the configuration this one replaces, so it leaves with it rather than
    // being held by a subscriber until the host answers again.
    this.applyMatch(null, false);
    this.listeners.forEach((listener) => listener(this.state));
  }

  private applyMatch(match: PersonalizationPricingMatch | null, fromHost: boolean) {
    if (fromHost && match && this.capability && match.sequence !== this.state.sequence) {
      return;
    }
    const sameValue =
      this.match?.variantId === match?.variantId &&
      this.match?.sequence === match?.sequence &&
      this.match?.reason === match?.reason;
    // A host null arrives while the match is already null (cleared when the configuration changed).
    // Value equality alone would drop that answer and leave the header on pending.
    if (sameValue && this.hostAnswered === fromHost) {
      return;
    }
    this.hostAnswered = fromHost;
    this.match = match;
    this.matchListeners.forEach((listener) => listener(this.match));
  }
}
